import { isProfileNative, normalizeProfileUser, profileAllowsArea } from './profile'
import { GET, POST, PUT } from './api'
import { ReadObject, SaveObject } from './storage'
import { captureSessionState, isSessionStateCurrent } from './session'
import { isLitePath } from 'utils/portal'

export const IDENTITY_AREA_SESSIONS = { universe: 'authentication', lite: 'mvu_essentials', accelerator: 'mvu_accelerator' }
export const IDENTITY_AREA_ROUTES = { universe: '/dashboard', lite: '/mvu-lite', accelerator: '/mvu-accelerator' }

let hydrationRevision = 0

export const HydrateIdentitySessions = async () => {
  const operation = ++hydrationRevision
  const snapshot = captureSessionState()
  if (!snapshot.writable) return false
  const current = () => operation === hydrationRevision && isSessionStateCurrent(snapshot)
  const sessions = Object.fromEntries(Object.entries(IDENTITY_AREA_SESSIONS).map(([area, key]) => [area, ReadObject(key)]))
  const path = window.location.pathname
  const sourceArea = isLitePath(path) && sessions.lite?.jwt ? 'lite'
    : path.startsWith('/mvu-accelerator') && sessions.accelerator?.jwt ? 'accelerator' : 'universe'
  const identity = await GET('/identity/session', true)
  if (!current()) return false
  const native = isProfileNative(identity)
  if (!identity?.enabled || !Array.isArray(identity.accesses) || !Number.isSafeInteger(identity.centralUserId)
    || (!native && !identity.accesses.some(access => access.enabled))) return false
  const selected = {}
  for (const access of identity.accesses) {
    if (!access.enabled || access.status !== 'active' || !Object.prototype.hasOwnProperty.call(IDENTITY_AREA_SESSIONS, access.area)) continue
    // Discovery already authenticated this source JWT. A secondary network
    // failure must not discard the successful login or invalidate its token.
    if (!native && access.area === sourceArea && sessions[sourceArea]?.jwt) {
      selected[access.area] = sessions[sourceArea]
      continue
    }
    const session = await POST('/identity/session', { area: access.area }, true)
    if (!current()) return false
    if (session?.jwt && session.identity?.centralUserId === identity.centralUserId
      && (!native || session.identity?.area === access.area && profileAllowsArea(session.user, access.area))) {
      selected[access.area] = native ? { ...session, user: normalizeProfileUser(session.user),
        ...(access.area === sourceArea && sessions[sourceArea]?.jwt ? { jwt: sessions[sourceArea].jwt } : {}) } : session
    } else if (!native && sessions[access.area]?.jwt && sessions[access.area]?.identity?.centralUserId === identity.centralUserId) {
      selected[access.area] = sessions[access.area]
    }
  }
  if (!current()) return false
  // Browser storage is synchronous: publish only after every awaited response
  // has been checked, so another account can never inherit a partial result.
  for (const [area, key] of Object.entries(IDENTITY_AREA_SESSIONS)) SaveObject(key, selected[area] || {})
  const areas = Object.keys(selected)
  SaveObject('identity_session', { centralUserId: identity.centralUserId, areas, ...(native ? { profileAuthority: 'profile' } : {}) })
  if (native) SaveObject('user', normalizeProfileUser(selected[sourceArea]?.user) || {})
  return { ...identity, areas }
}

export const ReadIdentitySession = () => ReadObject('identity_session')
export const ReadIdentityAccess = () => GET('/identity/session', true)
export const RequestUniverseAccess = profile => POST('/identity/requests', profile, true)
export const ReadManagedIdentities = ({ area, search, page }) => GET(`/identity/users?area=${encodeURIComponent(area)}&search=${encodeURIComponent(search || '')}&page=${page || 1}`, true)
export const ChangeIdentityAccess = (userId, area, status, expectedRevision) => PUT(`/identity/users/${userId}/access/${area}`, { status, expectedRevision }, true)
export const CreateIdentityInvitation = (userId, area, companyName) => POST(`/identity/users/${userId}/invitations/${area}`, { companyName }, true)
